import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Badge, Dropdown, Form, Tab, Table, Tabs } from "react-bootstrap";
import { renderToStaticMarkup } from "react-dom/server";
import { withTranslation } from "react-i18next";
import { BiBookOpen, BiCalendar, BiDotsHorizontalRounded, BiPencil, BiPlus, BiTrash, BiUser } from "react-icons/bi";
import { BsEye, BsFileEarmarkPdf, BsFileEarmarkText, BsFillGridFill, BsFileEarmarkCode } from "react-icons/bs";
import { FaList } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EventContext, LoaderContext, PollerContext, RegulationContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import Pagination from "../../../components/Pagination/Pagination";
import { AIReportJobStatus, DEFAULT_ITEMS_PER_PAGE, LS_AUTHORITY_KEY, PollerType, STATUS } from '../../../core/constants/common';
import useHasPermission from "../../../core/hooks/useHasPermission";
import CompanyService from "../../../core/service/CompanyService";
import ReportService from "../../../core/service/ReportService";
import {
  downloadValidationExcelReport,
  shouldBlockXbrlDownload,
} from "../../../core/utilities/brsrValidationReport";
import { generateBRSRPDFFromReport, generateBRSRWordFromReport } from "../../../core/utilities/BRSRUtils";
import ReportingUtils from "../../../core/utilities/ReportingUtils";
import {
  buildReportingListUrl,
  normalizeReportingListTab,
  parseReportingListPage,
  parseReportingListSize,
} from "../../../core/utilities/reportingListUrl";
import BRSRXbrlValidationModal from "./BRSR/BRSRXbrlValidationModal";
import TruncatedText from "../../../core/utilities/TruncatedText";
import Utils from "../../../core/utilities/Utils";
import GRI_pic from "../../../images/GRI.jpg";
import esg_ai_pic from "../../../images/esg_ai_Green.png";
import upgradeIcon from "../../../images/upgrade.png";
import GHGDataView from "./GHG/GHGDataView";
import NewReportModal from "./NewReportModal";
import ReportExportModal from "./ReportExportModal";
import ReportGenProgressCard from "./ReportGenProgressCard";
import './Reporting.scss';

const STANDARD_REPORT_VIEWS = ['GRI', 'ESRS', 'VSME', 'GHG', 'BRSR', 'UAE_FDL'];
const isStandardReportView = (view) => STANDARD_REPORT_VIEWS.includes(view);
const isEsrsStyleReportView = (view) => ['ESRS', 'VSME', 'GHG', 'BRSR', 'UAE_FDL'].includes(view);

const ReportingList = ({ t }) => {
  const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingUrlRef = useRef(false);
  const prevActiveViewRef = useRef(null);
  const { glbRegulation } = useContext(RegulationContext);
  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => parseReportingListSize(searchParams));
  const [currentPage, setCurrentPage] = useState(() => parseReportingListPage(searchParams));
  const companyId = lsDat.profile.company.id;
  const [regulation, setRegulation] = useState({});
  const [reports, setReports] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const { pollerData } = useContext(PollerContext);
  const { setLoaderDisplay } = useContext(LoaderContext);

  const [editedReport, setEditedReport] = useState('');
  const [reportIdToFinalize, setReportIdToFinalize] = useState(null);
  const [reportIdToDelete, setReportIdToDelete] = useState(null);
  const [reportToExport, setReportToExport] = useState({ id: 0 });
  const [xbrlValidationModal, setXbrlValidationModal] = useState(null);
  const [viewMode, setViewMode] = useState('LIST');

  const getTabFromQueryParam = (pageParam) => {
    const normalized = normalizeReportingListTab(pageParam);
    return normalized !== null ? normalized : 1;
  };

  const getQueryParamFromTab = (tabKey) => String(tabKey);

  const getInitialTab = () => {
    const pageParam = searchParams.get('page');
    if (pageParam !== null && pageParam !== '') {
      return getTabFromQueryParam(pageParam);
    }
    return 1;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  const { event } = useContext(EventContext);
  const company = lsDat.profile.company;
  const textRefs = useRef([]);
  const { hasGRIViewPermission, hasBRSRViewPermission, hasCSRViewPermission, hasVSMEViewPermission, hasGHGReportViewPermission,
    hasGRIUpdatePermission, hasBRSRUpdatePermission, hasCSRUpdatePermission, hasVSMEUpdatePermission, hasGHGReportUpdatePermission,
    hasUAEFDLReportViewPermission, hasUAEFDLReportUpdatePermission
  } = useHasPermission();

  const canUpdateReports = () => (
    hasGRIUpdatePermission() || hasBRSRUpdatePermission() || hasCSRUpdatePermission()
    || hasVSMEUpdatePermission() || hasGHGReportUpdatePermission() || hasUAEFDLReportUpdatePermission()
  );

  // Helper function to get the first available report type based on permissions
  const getFirstAvailableReportType = () => {
    // Priority order: GRI, BRSR, BRSR-OLD, GHG, UAE_FDL, ESRS, VSME
    if (hasGRIViewPermission()) return 'GRI';
    if (hasBRSRViewPermission()) return 'BRSR';
    if (hasBRSRViewPermission()) return 'BRSR-OLD';
    if (hasGHGReportViewPermission()) return 'GHG';
    if (hasUAEFDLReportViewPermission()) return 'UAE_FDL';
    if (hasCSRViewPermission()) return 'ESRS';
    if (hasVSMEViewPermission()) return 'VSME';
    return 'GRI'; // Default fallback - will show upgrade options
  };

  // Initialize activeView with URL parameter or first available report type
  const [activeView, setActiveView] = useState(() => {
    const urlView = searchParams.get('view');
    if (urlView) {
      // If URL has a view parameter, validate it against permissions
      const hasPermission = {
        'GRI': hasGRIViewPermission(),
        'BRSR': hasBRSRViewPermission(),
        'BRSR-OLD': hasBRSRViewPermission(),
        'GHG': hasGHGReportViewPermission(),
        'UAE_FDL': hasUAEFDLReportViewPermission(),
        'ESRS': hasCSRViewPermission(),
        'VSME': hasVSMEViewPermission()
      };

      if (hasPermission[urlView]) {
        return urlView;
      } else {
        // If user doesn't have permission for the URL view, redirect to first available
        const firstAvailable = getFirstAvailableReportType();
        // Update URL without page reload
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('view', firstAvailable);
        navigate(`?${newSearchParams.toString()}`, { replace: true });
        return firstAvailable;
      }
    }
    return getFirstAvailableReportType();
  });


  // Helper function to get job ID from report data
  const getJobIdFromReport = (report) => {
    // First try to get from report.job.id
    if (report?.job?.id) {
      return report.job.id;
    }

    // Fallback to report.Jobid (if it exists and is not null)
    if (report?.Jobid) {
      return report.Jobid;
    }

    // Try other possible field names
    if (report?.jobId) {
      return report.jobId;
    }

    if (report?.job_id) {
      return report.job_id;
    }

    // If still no job ID, try to get from localStorage
    const reportData = JSON.parse(localStorage.getItem('currentReport') || '{}');
    if (reportData?.job?.id) {
      return reportData.job.id;
    }

    return null;
  };

  const updateListUrlParams = (state = {}) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', activeView);
    newSearchParams.set('page', getQueryParamFromTab(activeTab));

    const listPage = state.listPage ?? currentPage;
    const size = state.size ?? itemsPerPage;

    if (listPage > 1) {
      newSearchParams.set('listPage', String(listPage));
    } else {
      newSearchParams.delete('listPage');
    }

    if (size !== DEFAULT_ITEMS_PER_PAGE) {
      newSearchParams.set('size', String(size));
    } else {
      newSearchParams.delete('size');
    }

    isUpdatingUrlRef.current = true;
    setSearchParams(newSearchParams, { replace: true });
  };

  // Update URL when activeView changes; reset pagination when the view changes
  useEffect(() => {
    const viewChanged = prevActiveViewRef.current !== null && prevActiveViewRef.current !== activeView;
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', activeView);

    if (viewChanged) {
      newSearchParams.delete('listPage');
      newSearchParams.delete('size');
      setCurrentPage(1);
      isUpdatingUrlRef.current = true;
      setSearchParams(newSearchParams, { replace: true });
      if (glbRegulation?.id) {
        listReports(glbRegulation.id, 1, itemsPerPage, activeView, activeTab);
      }
    } else if (searchParams.get('view') !== activeView) {
      setSearchParams(newSearchParams, { replace: true });
    }

    prevActiveViewRef.current = activeView;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Sync active tab when query param changes (e.g., browser back/forward)
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const tabFromQuery = getTabFromQueryParam(pageParam);

    if (tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (tabKey) => {
    const tab = +tabKey;
    setActiveTab(tab);
    setCurrentPage(1);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', getQueryParamFromTab(tab));
    newSearchParams.set('view', activeView);
    newSearchParams.delete('listPage');
    newSearchParams.delete('size');
    isUpdatingUrlRef.current = true;
    setSearchParams(newSearchParams);
    if (glbRegulation?.id) {
      listReports(glbRegulation.id, 1, itemsPerPage, activeView, tab);
    }
  };

  useEffect(() => {
    if (!glbRegulation?.id) return;

    if (isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = false;
      setRegulation(glbRegulation);
      loadBusinessUnits();
      return;
    }

    const listPage = parseReportingListPage(searchParams);
    const size = parseReportingListSize(searchParams);

    setCurrentPage(listPage);
    setItemsPerPage(size);
    listReports(glbRegulation.id, listPage, size, activeView, activeTab);
    setRegulation(glbRegulation);
    loadBusinessUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glbRegulation?.id, activeView, event, activeTab, searchParams]);

  const listReports = (
    regulationId,
    pageNumber,
    itemsPerPageVal,
    view,
    status
  ) => {
    const requestedPage = pageNumber > 0 ? pageNumber : 1;
    let apiPageNumber = requestedPage - 1;
    if (pageNumber <= 0) {
      setCurrentPage(1);
      apiPageNumber = 0;
    }
    let perPage;
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }
    let req;
    if (view === 'GRI') {
      req = ReportService.listReports(
        apiPageNumber,
        perPage,
        regulationId,
        companyId,
        status,
        '',
        '',
        'GRI'
      );
    } else if (view === 'ESRS') {
      req = ReportService.listReports(
        apiPageNumber,
        perPage,
        regulationId,
        companyId,
        status,
        ['USER', 'AI'].toString(),
        '',
        'ESRS'
      );
    } else if (view === 'BRSR') {
      req = ReportService.listReports(
        apiPageNumber,
        perPage,
        regulationId,
        companyId,
        status,
        '',
        '',
        'BRSR'
      );
    } else if (view === 'UAE_FDL') {
      req = ReportService.listReports(
        apiPageNumber,
        perPage,
        regulationId,
        companyId,
        status,
        ['USER', 'AI'].toString(),
        '',
        'UAE_FDL'
      );
    } else if (view === 'BRSR-OLD') {
      // Studio grouped reports API — only supports type, pagination, and sort (no regulation/status filters)
      req = ReportService.listGroupedStudioReports(
        'BRSR',
        apiPageNumber,
        perPage,
        ''
      );
    } else if (view === 'GHG') {
      req = ReportService.listGHGReports(
        apiPageNumber,
        perPage,
        regulationId,
        companyId,
        status,
        ['USER', 'AI'].toString(),
        ''
      );
    } else if (view === 'VSME') {
      req = ReportService.listVSMEReports(
        apiPageNumber,
        perPage,
        regulationId,
        companyId,
        status,
        ['USER', 'AI'].toString(),
        ''
      );
    }
    req?.then((response) => {
      if (view === 'BRSR-OLD') {
        setReports(response.data?.data?.content ?? []);
        setItemTotalCount(response.data?.data?.totalElements ?? 0);
      } else {
        const reportData = Array.isArray(response.data) ? response.data : [];
        const totalCount = parseFloat(response.headers["x-total-count"]) || 0;
        setReports(reportData);
        setItemTotalCount(totalCount);

        // Recover when the current page is empty but other pages still have data
        if (reportData.length === 0 && totalCount > 0 && requestedPage > 1) {
          const previousPage = requestedPage - 1;
          setCurrentPage(previousPage);
          listReports(regulationId, previousPage, perPage, view, status);
        }
      }
    }).catch((error) => {
    });
  };

  const loadBusinessUnits = async () => {
    try {
      const res = await CompanyService.getEnterprisesByLevel(1);
      if (res.status === 200) {
        const sortedBusinessUnits = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
        setBusinessUnits(sortedBusinessUnits);
      }
    } catch (error) {
      console.error('Error loading business units:', error);
    }
  };

  const getEntityNames = (entities) => {
    if (!entities || !Array.isArray(entities) || entities.length === 0) return 'N/A';

    // If business units haven't loaded yet, show only names from entity objects
    if (businessUnits.length === 0) {
      return entities.map(entity =>
        typeof entity === 'object' ? entity.name : entity
      ).filter(name => name).join(', ') || 'N/A';
    }

    return entities.map(entity => {
      const entityId = typeof entity === 'object' ? entity.id : entity;
      const businessUnit = businessUnits.find(bu => bu.id === entityId);
      return businessUnit ? businessUnit.name : (typeof entity === 'object' ? entity.name : null);
    }).filter(name => name).join(', ') || 'N/A';
  };

  const generatePDF = (report, openPreview = false) => {
    // For BRSR reports, use browser print functionality instead of API
    if (isBRSRReport(report)) {
      generateBRSRPDFFromReport(report, setLoaderDisplay, (error) => {
        toast.error(error.message || 'Error generating PDF', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
      return;
    }

    // For non-BRSR reports, use the existing API call
    setLoaderDisplay(true);
    ReportService.generateESRSPDF(report.id)
      .then((res) => {
        setLoaderDisplay(false);
        let blob = new Blob([res.data], { type: 'application/pdf' });
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = fileURL;
        if (openPreview) {
          link.target = '_blank';
        } else {
          link.download = `${report.name}.pdf`;
        }
        link.click();
      })
      .catch((error) => {
        setLoaderDisplay(false);
        toast.error(error.title, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const downloadXbrlFile = (xbrl, report, fullReport) => {
    const financialYear = Utils.getYearFrom(fullReport?.period || report?.period);
    const fileName = `BRSR_XBRL_${companyId}_${financialYear}.xml`;
    const blob = new Blob([xbrl], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const generateXBRL = async (report) => {
    setLoaderDisplay(true);
    try {
      let fullReport = report;
      if (!report.sections || report.sections.length === 0) {
        const res = await ReportService.getBRSRReport(report.id);
        if (res.status !== 200) {
          throw new Error('Failed to fetch report');
        }
        fullReport = res.data;
      }

      const res = await ReportService.generateBRSRXBRL(fullReport);
      if (res?.data?.status === 'SUCCESS' && res.data.xbrl) {
        const validation = res.data.validation;
        if (shouldBlockXbrlDownload(validation)) {
          setXbrlValidationModal({
            xbrl: res.data.xbrl,
            validation,
            report,
            fullReport,
          });
        } else {
          downloadXbrlFile(res.data.xbrl, report, fullReport);
        }
      } else {
        throw new Error('Invalid XBRL response from server');
      }
    } catch (error) {
      toast.error(error.message || 'Error generating XBRL report', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderDisplay(false);
    }
  };

  const renderAIReportGenerationProgressCard = () => {
    return (
      <>
        {
          pollerData.type === PollerType.REPORT_BUILDER &&
          (pollerData.status === AIReportJobStatus.IN_PROGRESS || pollerData.status === AIReportJobStatus.NOT_STARTED) &&
          pollerData.jobType === 'AUTO_DRAFT' &&
          <div className="col-lg-10 p-1 ps-0">
            <ReportGenProgressCard stateCount={pollerData.jobType === 'AUTO_DRAFT' ? 8 : 4} />
          </div>
        }
      </>
    );
  };

  const finalizeReport = (id) => {
    ReportService.finalizeReport(id)
      .then((res) => {
        if (res.status !== 200) return;

        toast.success("Success", {
          position: toast.POSITION.TOP_RIGHT,
        });
        listReports(glbRegulation.id, currentPage, itemsPerPage, activeView, activeTab);
      })
      .catch((error) => {
        toast.error(error.title, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const deleteReport = (id) => {
    setLoaderDisplay(true);
    ReportService.deleteESRSReport(id)
      .then((res) => {
        if (res.status < 200 || res.status >= 300) {
          setLoaderDisplay(false);
          return;
        }
        setLoaderDisplay(false);
        toast.success("Success", {
          position: toast.POSITION.TOP_RIGHT,
        });
        const pageToLoad = reports.length <= 1 && currentPage > 1
          ? currentPage - 1
          : currentPage;
        if (pageToLoad !== currentPage) {
          setCurrentPage(pageToLoad);
        }
        listReports(glbRegulation.id, pageToLoad, itemsPerPage, activeView, activeTab);
      })
      .catch((error) => {
        setLoaderDisplay(false);
        toast.error(error.title, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const editStudioBasedReport = (regulation, report) => {
    if (activeView === 'BRSR-OLD') {
      navigate('../BRSR?regulationId=' + regulation.id
        + '&title=' + report.title
        + '&year=' + new Date(report.period).getFullYear()
        + '&stdType=BRSR');
    } else if (activeView === 'GHG') {
      navigate('../ghg-report?regulationId=' + regulation.id
        + '&title=' + report.title
        + '&year=' + new Date(report.period).getFullYear()
        + '&stdType=GHG');
    }
  };

  const downloadStudioBasedReport = (type, regulation, report) => {
    if (activeView === 'GHG') {
      downloadGHGReport(type, regulation, report);
    }
  };


  const downloadGHGReport = (type, regulation, report) => {
    setLoaderDisplay(true);
    const yearFilter = new Date(report.period).getFullYear();
    const page1Req = getPageRequest(1, `${yearFilter}-01-01`, regulation.id, report.title);

    Promise.all([
      page1Req
    ]).then(res => {
      const p1Data = {};
      res[0].data.data?.forEach(d => {
        p1Data[d.key] = d;
      });
      setLoaderDisplay(false);
      // Get GHG data HTML
      const renderedHTML = renderToStaticMarkup(
        <GHGDataView
          ghgContentRef={null}
          company={company}
          yearFilter={yearFilter}
          page1={p1Data} />
      );
      if (type === 'PDF') {
        const input = document.createElement("div");
        input.innerHTML = renderedHTML;
        ReportingUtils.downloadBRSRReportPDF(input, `GHG_${yearFilter}.pdf`);
      } else {
        ReportingUtils.downloadBRSRReportWord(renderedHTML, `GHG_${yearFilter}.docx`);
      }
    }).catch(err => { setLoaderDisplay(false); });
  };

  const getPageRequest = (pageId, period, regulationId, reportTitle) => {
    return ReportService.getReportTemplateData({
      pageId: pageId,
      standardId: regulationId,
      period: period,
      title: reportTitle,
      type: activeView
    });
  };

  const onEditReport = (report) => {
    if (report?.status === 'DRAFT') {
      setLoaderDisplay(true);
      let periodEnd = report.periodEnd;
      if (!periodEnd || periodEnd === 'Invalid date') {
        const parsedYear = Utils.getYearFrom(report.period);
        periodEnd = `${parsedYear}-12-31`;
      }
      
      // For BRSR reports, use calendar year period calculation
      let periodStart = report.period;
      if (isBRSRReport(report)) {
        const parsedYear = Utils.getYearFrom(report.period);
        periodStart = `${parsedYear}-01-01`;
        periodEnd = `${parsedYear}-12-31`;
      }
      
      ReportService.getDataCompletionProgress(glbRegulation?.id,
        moment(periodStart).format('YYYY-MM-DD'), true,
        periodStart, periodEnd, report.entities).then(res => {
          setLoaderDisplay(false);
          if (res.status !== 200) {
            // If data completion check fails, just navigate to edit
            if (isBRSRReport(report)) {
              navigateToReport(report);
            } else {
              setEditedReport(report);
            }
            return;
          }
          let changed = false;
          for (let index = 0; index < res.data.data?.length; index++) {
            const element = res.data.data[index];
            const inboundDate = moment(element?.lastModifiedDate);
            const reportDate = moment(report?.lastModifiedDate);
            if (inboundDate.isAfter(reportDate)) {
              changed = true;
              break;
            }
          }
          if (changed) {
            // Data has changed, show modal with data completion progress
            setEditedReport({ ...report, dataCompletionProgress: res.data.data });
          } else {
            // Data hasn't changed, directly navigate to edit
            if (isBRSRReport(report)) {
              navigateToReport(report);
            } else {
              fetchReportAndNavigate(report);
            }
          }
        }).catch(error => {
          setLoaderDisplay(false);
          // On error, just navigate to edit
          if (isBRSRReport(report)) {
            navigateToReport(report);
          } else {
            setEditedReport(report);
          }
        })
    } else {
      setEditedReport(report);
    }
  };

  const callbackFinalizeReport = (boolVal) => {
    setReportIdToFinalize(0);
    boolVal && finalizeReport(reportIdToFinalize);
  };

  const callbackDeleteReport = (boolVal, id) => {
    const deleteId = id || reportIdToDelete;
    setReportIdToDelete(null);
    if (boolVal && deleteId) {
      deleteReport(deleteId);
    }
  };

  const handleUpgradeClick = (viewType) => {
    const routes = {
      'GRI': '/company_admin/gri-upsale',
      'BRSR': '/company_admin/brsr-upsale',
      'BRSR-OLD': '/company_admin/brsr-upsale',
      'GHG': '/company_admin/ghg-upsale',
      'UAE_FDL': '/company_admin/ghg-upsale',
      'ESRS': '/company_admin/csrd-upsale',
      'VSME': '/company_admin/vsme-upsale'
    };

    const route = routes[viewType];
    if (route) {
      navigate(route);
    } else {
      console.warn(`No upgrade route found for view type: ${viewType}`);
    }
  };

  const renderActions = () => {
    return (
      <div>
        <div className="List_Table_01 mx-2">
          <div className="List_table-card01_head p-0">
            <div className="List_table-card_Assessment">
              <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("GRI.label")}</span>
                    {!hasGRIViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to GRI" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-2"
                value={'GRI'}
                checked={activeView === 'GRI' ? "checked" : ''}
                onChange={() => hasGRIViewPermission() ? setActiveView('GRI') : handleUpgradeClick('GRI')}
              />
              <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("BRSR.label")}</span>
                    {!hasBRSRViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to BRSR" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-1"
                value={'BRSR'}
                checked={activeView === 'BRSR' ? "checked" : ''}
                onChange={() => hasBRSRViewPermission() ? setActiveView('BRSR') : handleUpgradeClick('BRSR')}
              />
              {/* <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("BRSROLD.label")}</span>
                    {!hasBRSRViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to BRSR-OLD" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-1-old"
                value={'BRSR-OLD'}
                checked={activeView === 'BRSR-OLD' ? "checked" : ''}
                onChange={() => hasBRSRViewPermission() ? setActiveView('BRSR-OLD') : handleUpgradeClick('BRSR-OLD')}
              /> */}
              <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("GHG.label")}</span>
                    {!hasGHGReportViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to GHG" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-3"
                value={'GHG'}
                checked={activeView === 'GHG' ? "checked" : ''}
                onChange={() => hasGHGReportViewPermission() ? setActiveView('GHG') : handleUpgradeClick('GHG')}
              />
              <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("UAE_FDL.label")}</span>
                    {!hasUAEFDLReportViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to UAE-FDL" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-uae-fdl"
                value={'UAE_FDL'}
                checked={activeView === 'UAE_FDL' ? "checked" : ''}
                onChange={() => hasUAEFDLReportViewPermission() ? setActiveView('UAE_FDL') : handleUpgradeClick('UAE_FDL')}
              />
              <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("CSRD.label")}</span>
                    {!hasCSRViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to CSRD" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-4"
                value={'ESRS'}
                checked={activeView === 'ESRS' ? "checked" : ''}
                onChange={() => hasCSRViewPermission() ? setActiveView('ESRS') : handleUpgradeClick('ESRS')}
              />
              <Form.Check
                inline
                label={
                  <div className="d-flex align-items-center">
                    <span>{t("VSME.label")}</span>
                    {!hasVSMEViewPermission() && (
                      <img src={upgradeIcon} alt="upgrade" className="upgrade-icon"
                        title="Upgrade to VSME" />
                    )}
                  </div>
                }
                name="stdType"
                type="radio"
                id="inline-radio-5"
                value={'VSME'}
                checked={activeView === 'VSME' ? "checked" : ''}
                onChange={() => hasVSMEViewPermission() ? setActiveView('VSME') : handleUpgradeClick('VSME')}
              />
            </div>
            {canUpdateReports() && (
              <button
                type="button"
                className="btn btn-primary mx-2"
                disabled={pollerData.type === PollerType.REPORT_BUILDER &&
                  (pollerData.status === AIReportJobStatus.IN_PROGRESS || pollerData.status === AIReportJobStatus.NOT_STARTED)}
                onClick={() => {
                  setEditedReport({ id: 0 });
                }}>
                <BiPlus /> {t("New.label")}
              </button>
            )}
            {/* <Button
              variant="secondary" size="sm" >
              <CgSortAz className="" /> {t("Filter.label")}
            </Button> */}
            <div className="List_table-card_Assessment ms-2">
              <Form.Check
                inline
                label={<BsFillGridFill />}
                type="radio"
                id="viewMode1"
                value={'GRID'}
                checked={viewMode === 'GRID' ? "checked" : ''}
                onChange={() => setViewMode('GRID')} />
              <Form.Check
                inline
                label={<FaList />}
                type="radio"
                id="viewMode2"
                value={'LIST'}
                checked={viewMode === 'LIST' ? "checked" : ''}
                onChange={() => setViewMode('LIST')} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderListView = () => {
    return (
      <Table>
        <thead>
          <tr>
            <th className="task-title-col">{t("ReportName.label")}</th>
            {/* <th>{t('ReportingPeriod.label')}</th> */}
            <th>{t("Type.label")}</th>
            <th>{t("Entities.label")}</th>
            <th>{t("ReportingPeriod.label")}</th>
            <th>{t("LastModified.label")}</th>
            {/* <th>{t("Status.label")}</th> */}
            <th>{t("CreatedBy.label")}</th>
            {isStandardReportView(activeView) && <th></th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {reports?.length > 0 ? (
            reports?.map((report, index) => (
              <tr key={report.id}>
                <td className="task-title-col">
                  <div className="d-flex align-items-center">
                    {isStandardReportView(activeView) && <TruncatedText text={report.name}></TruncatedText>}
                    {(activeView === 'BRSR-OLD') && <TruncatedText text={report.title}></TruncatedText>}
                    {
                      isStandardReportView(activeView) &&
                      report.type === 'AI' &&
                      <span className="AI_list_Icon">
                        <img src={esg_ai_pic} />
                      </span>
                    }
                  </div>
                </td>
                {/* <td>{Utils.getYearFrom(report.period)}</td> */}

                <td className="white-nowrap">
                  {report.basis === 'ENTITY' ? 'Entity Report' :
                    report.basis === 'ORG' ? 'Organization Report' :
                      report.basis || '-'}
                </td>
                <td>{getEntityNames(report.entities)}</td>
                <td className="white-nowrap">
                  {report.period && report.periodEnd
                    ? (() => {
                      const startDate = moment(report.period);
                      const endDate = moment(report.periodEnd);
                      if (startDate.isValid() && endDate.isValid()) {
                        return `${startDate.format("MMM YYYY")}-${endDate.format("MMM YYYY")}`;
                      } else {
                        return '-';
                      }
                    })()
                    : report.period
                      ? (() => {
                        const date = moment(report.period);
                        return date.isValid() ? date.format("MMM DD, YYYY") : '-';
                      })()
                      : '-'
                  }
                </td>
                <td className="white-nowrap"> {report.lastModifiedDate ? moment(report.lastModifiedDate).format("MMM DD, YYYY") : ""}</td>
                
                <td>{report?.createdBy ?? report.creator?.name}</td>
                {
                  isStandardReportView(activeView) &&
                  <td>
                    {(() => {

                      switch (report.status) {
                        case "DRAFT":
                          return (
                            <button className="btn btn-primary btn-sm" hidden={!canUpdateReports()}
                              onClick={() => {
                                setReportIdToFinalize(report.id)
                              }
                              }>{t('Finalize.label')}</button>
                          );
                      }
                    })()}
                  </td>
                }
                <td>
                  {isStandardReportView(activeView) && (report.active || (activeView === 'BRSR' && report.status === 'NOT_STARTED') || report.status === 'FAILED') && renderGRIReportMoreOptions(report)}
                  {(activeView === 'BRSR-OLD') && renderStudioBasedReportMoreOptions(report)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" className="text-center">
                <div className="Nodatafound_box">
                  <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    );
  };

  const renderGridView = () => {
    return (
      <div className="Reporting_Studio_gridbox">
        <div className="row m-0">
          {
            reports.length === 0 &&
            <div className="Nodatafound_box">
              <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
            </div>
          }
          {
            reports?.map((report, index) => {
              return (
                <div className='col-md-3 p-2'
                  key={`reportCard-${index + 1}`}>
                  <div className={`card Report_Studio_Singleview ${report.status}`}>
                    <div className="card-body">
                      <span className="	Reporting_Period">
                        {Utils.getYearFrom(report.period)}
                      </span>
                      <div className="Report_pic">
                        <img src={GRI_pic} alt="Report Logo" />
                        <span className="Reporting_StatusBox">
                          {(() => {
                            switch (report.status) {
                              case "FINALIZED":
                                return (<Badge bg="badge-Finalized">{STATUS['FINALIZED']}</Badge>);
                              case "FAILED":
                                return (<Badge bg="badge-Danger">{STATUS['FAILED']}</Badge>);
                              case "DRAFT":
                              default:
                                if (report.type === 'AI' && !report.active) {
                                  return (
                                    <Badge bg="badge-Draft">
                                      {STATUS['GENERATING_REPORT']}
                                    </Badge>
                                  );
                                } else {
                                  return (
                                    <Badge bg={
                                      report.status === 'DRAFT' ? 'badge-Inactive' :
                                        report.status === 'GENERATING_REPORT' ? 'badge-Inprogress' :
                                          'badge-New'
                                    }>
                                      {STATUS[report.status]}
                                    </Badge>
                                  );
                                }
                            }
                          })()}
                        </span>
                      </div>
                      <div className="RS_Singleview_dt">
                        {
                          isStandardReportView(activeView) &&
                          report.type === 'AI' &&
                          <span className="AI_Grid_Icon">
                            <img src={esg_ai_pic} />
                          </span>
                        }
                        <div className="RS_Singleview_dt_in">
                          <h6>
                            {isStandardReportView(activeView) && <TruncatedText text={report.name}></TruncatedText>}
                            {(activeView === 'BRSR-OLD') && <TruncatedText text={report.title}></TruncatedText>}
                          </h6>
                          <ul>
                            <li>
                              <BiUser /> {report.creator?.name ?? report?.createdBy}
                            </li>
                            <li> / </li>
                            <li>
                              <BiCalendar /> {moment(report?.lastModifiedDate).format('MMM DD, YYYY')}
                            </li>
                          </ul>
                          <span className="more_options">
                            {isStandardReportView(activeView) && (report.active || report.status === 'FAILED') && renderGRIReportMoreOptions(report)}
                            {(activeView === 'BRSR-OLD') && renderStudioBasedReportMoreOptions(report)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          }
          {itemsTotalCount > 10 ? (
            <div className="p-2 d-flex align-items-center justify-content-end">
              <Pagination
                itemsTotalCount={itemsTotalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                alwaysShown={true}
                setItemsPerPage={setItemsPerPage}
                pageClicked={(pageNum, perPage) => {
                  const page = pageNum > 0 ? pageNum : 1;
                  const size = perPage > 0 ? perPage : itemsPerPage;
                  listReports(regulation.id, pageNum, perPage, activeView, activeTab);
                  updateListUrlParams({ listPage: page, size });
                }}
              />
            </div>
          ) : null}

        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    return (
      <div className="List_Table_01">
        <div className={`List_table-card01 ${viewMode === 'LIST' ? 'card' : ''}`}>
          {
            viewMode === 'LIST' &&
            <>
              {renderListView()}
              {
                itemsTotalCount > 10 &&
                <div className="p-2 List_footer_count">
                  <Pagination
                    itemsTotalCount={itemsTotalCount}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    alwaysShown={true}
                    setItemsPerPage={setItemsPerPage}
                    pageClicked={(pageNum, perPage) => {
                      const page = pageNum > 0 ? pageNum : 1;
                      const size = perPage > 0 ? perPage : itemsPerPage;
                      listReports(regulation.id, pageNum, perPage, activeView, activeTab);
                      updateListUrlParams({ listPage: page, size });
                    }}
                  />
                </div>
              }
            </>
          }
          {viewMode === 'GRID' && renderGridView()}
        </div>
      </div>
    );
  };

  const renderGRIReportMoreOptions = (report) => {
    return (
      <Dropdown>
        <Dropdown.Toggle
          variant="blank_outline"
          id="task-list-dropdown">
          <BiDotsHorizontalRounded />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {report.status !== "FINALIZED" && (
            <Dropdown.Item
              onClick={() => onEditReport(report)}
              hidden={!canUpdateReports()}
            >
              {report.status !== "DRAFT" ? (
                <>
                  <BiPencil className="me-1" /> {t("Edit.label")}
                </>
              ) : (
                <>
                  <BiBookOpen className="me-1" /> {t("Open.label")}
                </>
              )}
            </Dropdown.Item>
          )}
          {report.status != "NOT_STARTED" && report.status !== "FAILED" && (<>

            <Dropdown.Item onClick={() => generatePDF(report, true)}>
              <BsEye className="me-1" /> {t("Preview.label")}
            </Dropdown.Item>
            <Dropdown.Item onClick={() => generatePDF(report)}>
              <BsFileEarmarkPdf className="me-1" /> {t("DownloadAsPDF.label")}
            </Dropdown.Item>
            {isBRSRReport(report) && (
              <Dropdown.Item onClick={() => generateXBRL(report)}>
                <BsFileEarmarkCode className="me-1" /> {t("DownloadAsXBRL.label")}
              </Dropdown.Item>
            )}
            <Dropdown.Item onClick={() => {
              // For BRSR reports, use direct Word generation instead of modal
              if (isBRSRReport(report)) {
                generateBRSRWordFromReport(report, setLoaderDisplay, (error) => {
                  toast.error(error.message || 'Error generating Word document', {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                });
              } else {
                setReportToExport({ id: report.id, exportAs: 'WORD' });
              }
            }}>
              <BsFileEarmarkText className="me-1" /> {t("DownloadAsWord.label")}
            </Dropdown.Item>
          </>)}
          {
            // Show delete button for ESRS/VSME/GHG/BRSR reports OR for Draft/Not Started/Failed reports in any view
            (isEsrsStyleReportView(activeView) ||
              (report.status === 'DRAFT' || report.status === 'NOT_STARTED' || report.status === 'FAILED'))
              ?
              <Dropdown.Item onClick={() => setReportIdToDelete(report.id)}>
                <BiTrash className="me-1" /> {t("Delete.label")}
              </Dropdown.Item>
              : <></>
          }
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderStudioBasedReportMoreOptions = (report) => {
    return (
      <Dropdown>
        <Dropdown.Toggle
          variant="blank_outline"
          id="task-list-dropdown">
          <BiDotsHorizontalRounded />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => editStudioBasedReport(regulation, report)} hidden={!canUpdateReports()}>
            {t("Edit.label")}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => downloadStudioBasedReport('PDF', regulation, report)}>
            {t("DownloadAsPDF.label")}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => downloadStudioBasedReport('WORD', regulation, report)}>
            {t("DownloadAsWord.label")}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  // Helper function to navigate to reporting page (for BRSR - no need to fetch report first)
  const navigateToReport = (report, jobId = '') => {
    navigate('/company_admin/reporting?id=' + report.id
      + '&mode=EDIT&regulationId=' + regulation.id
      + '&stdType=' + activeView
      + '&year=' + Utils.getYearFrom(report.period)
      + (jobId ? '&jobId=' + jobId : '')
      + '&activeTab=' + activeTab
      + '&listPage=' + currentPage
      + (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE ? '&size=' + itemsPerPage : '')
      + '&source=reporting-list',
      { state: null });
  };

  // Helper function to fetch report and navigate to editing page (for non-BRSR reports that need job ID)
  const fetchReportAndNavigate = (report) => {
    setLoaderDisplay(true);
    ReportService.getReport(report.id)
      .then(reportResponse => {
        setLoaderDisplay(false);
        if (reportResponse.status === 200) {
          const fullReport = reportResponse.data;
          const jobId = getJobIdFromReport(fullReport);

          navigateToReport(report, jobId);
        } else {
          // Fallback to navigation without job ID
          navigateToReport(report);
        }
      })
      .catch(error => {
        setLoaderDisplay(false);
        // Fallback to navigation without job ID
        navigateToReport(report);
      });
  };

  const isBRSRReport = (r) => {
    return r?.type === 'BRSR' || r?.reportType === 'BRSR' || activeView === 'BRSR';
  };

  return (
    <div className="ReportingAndDisclosures_box">
      <div className="List_header">
        <h5 className="MH">{t("ReportingStudio.label")}</h5>
        {renderActions()}
      </div>
      <div className="ReportingAndDisclosures_box_inner">
        {renderAIReportGenerationProgressCard()}
        <div className="col-lg-12 p-1 ps-0 mt-1 report-list-container" id={regulation.id}>
          {
            isStandardReportView(activeView)
              ?
              <Tabs
                id="reportingListTabs"
                className="mb-1"
                activeKey={activeTab}
                onSelect={handleTabChange}>
                <Tab eventKey={1} title={t("Finalized.label")}>{renderTabContent()}</Tab>
                <Tab eventKey={0} title={t("Draft.label")}>{renderTabContent()}</Tab>
                <Tab eventKey={2} title={t("NotStarted.label")}>{renderTabContent()}</Tab>
                <Tab eventKey={3} title={t("Failed.label")}>{renderTabContent()}</Tab>
              </Tabs>
              :
              activeView === 'BRSR-OLD'
                ?
                renderTabContent()
                : <></>
          }
        </div>
      </div>
      {
        editedReport.id >= 0 &&
        <NewReportModal onHide={() => setEditedReport('')} activeView={activeView}
          view={activeView}
          editedReport={editedReport}
          activeTab={activeTab}
          reportGenerationCallback={() => {
            listReports(glbRegulation.id, 1, DEFAULT_ITEMS_PER_PAGE, activeView, activeTab)
          }} />
      }
      {
        reportIdToFinalize > 0 && (
          <ConfirmationModal
            modalShow={true}
            messageText={t("FinalizeReportModal.label")}
            callbackModal={callbackFinalizeReport}
            confirmBtn={t("Yes.label")}
            CancelBtn={t("No.label")}
          />
        )
      }
      {
        reportToExport?.id > 0 &&
        <ReportExportModal
          reportId={reportToExport?.id}
          exportAs={reportToExport?.exportAs}
          onHide={() => setReportToExport({ id: 0 })}
          activeView={activeView}
        />
      }
      {
        reportIdToDelete > 0 && (
          <ConfirmationModal
            modalShow={true}
            messageText={t("DeleteReportModal.label")}
            callbackModal={callbackDeleteReport}
            id={reportIdToDelete}
            confirmBtn={t("Yes.label")}
            CancelBtn={t("No.label")}
          />
        )
      }
      {xbrlValidationModal && (
        <BRSRXbrlValidationModal
          show={!!xbrlValidationModal}
          validation={xbrlValidationModal.validation}
          onHide={() => setXbrlValidationModal(null)}
          onDownloadExcel={() => {
            if (xbrlValidationModal?.validation) {
              downloadValidationExcelReport(xbrlValidationModal.validation);
            }
          }}
          onDownloadXbrlAnyway={() => {
            const { xbrl, report, fullReport } = xbrlValidationModal;
            downloadXbrlFile(xbrl, report, fullReport);
            setXbrlValidationModal(null);
          }}
        />
      )}
    </div>
  );
};

export default withTranslation()(ReportingList);
